import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { get, startCase } from 'lodash';

import { QB_OPERATOR_LABELS, QBOperator } from './QueryBuilder.constants';
import type {
  QBFilter,
  QBFilterDefinitionsResponseDTO,
  QBFilterOptions,
  QBFiltersConfig,
  QBLabelReplacement
} from './QueryBuilder.types';

dayjs.extend(utc);

export const isQBInOperator = (operator: string): boolean =>
  [QBOperator.In, QBOperator.NotIn].includes(operator as QBOperator);

export const isQBContainsOperator = (operator: string): boolean =>
  [
    QBOperator.ILike,
    QBOperator.Like,
    QBOperator.Contains,
    QBOperator.Overlap,
    QBOperator.ContainedBy,
    QBOperator.IStartsWith
  ].includes(operator as QBOperator);

/** Extract field names from the filter definitions sorted by the displayed label */
export const getUniqueBaseFields = (config: QBFiltersConfig): string[] =>
  Object.keys(config).sort((a, b) =>
    config[a].label.localeCompare(config[b].label)
  );

/** Get a human-readable label for an operator string */
export const getOperatorDescription = (operator: string): string =>
  QB_OPERATOR_LABELS[operator as QBOperator] ??
  operator
    .replace(/__/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

/** Get available operators for a specific base_field */
export const getOperatorsForField = (
  config: QBFiltersConfig,
  baseField: string
): QBFilterOptions[] => {
  const fieldDef = config[baseField];
  if (!fieldDef) return [];

  return fieldDef.operators.map((operator) => ({
    value: operator === '' ? QBOperator.Eq : operator,
    label: getOperatorDescription(operator === '' ? QBOperator.Eq : operator)
  }));
};

export const getFieldOptions = (
  config: QBFiltersConfig,
  baseField: string
): QBFilterOptions[] => {
  const fieldDef = config[baseField];
  if (!fieldDef) return [];

  if (fieldDef.options && fieldDef.type !== 'bool') return fieldDef.options;

  if (fieldDef.type === 'bool')
    return [
      {
        label: 'Yes',
        value: 'true',
        count:
          config[baseField].options?.find((opt) => opt.value === 'true')
            ?.count ?? 0
      },
      {
        label: 'No',
        value: 'false',
        count:
          config[baseField].options?.find((opt) => opt.value === 'false')
            ?.count ?? 0
      }
    ];

  return [];
};

export const getSelectedOptions = (
  filter: QBFilter | undefined,
  options: QBFilterOptions[]
): QBFilterOptions[] => {
  if (!filter) return [];

  const { operator, value } = filter;

  // Fall back to a synthesized option (using the raw value as its own label)
  // when a selected value is no longer present in the options list. This
  // happens for data-driven/implicit options (e.g. group-by counts) where a
  // previously selected value can be excluded once its count drops to zero.
  const findOrSynthesize = (v: string): QBFilterOptions =>
    options.find((opt) => String(opt.value) === v) ?? { value: v, label: v };

  if (isQBInOperator(operator)) {
    const values = String(value).split(',').filter(Boolean);
    return values.map(findOrSynthesize);
  }

  if (value === null || value === undefined || value === '') return [];

  return [findOrSynthesize(String(value))];
};

export const mapSearchParamsToQBFilters = (filters: string): QBFilter[] => {
  if (!filters) return [];

  try {
    const parsed: Record<string, [string, string]> = JSON.parse(filters);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      console.warn('Parsed filters is not an object:', parsed);
      return [];
    }
    return Object.entries(parsed)
      .map(([baseField, value]) => {
        const operator = value[0];

        if (value[0] === undefined || value[1] === undefined) {
          console.warn(
            `Invalid filter entry for base field "${baseField}". Missing field definition or operator/value.`
          );
          return null;
        }

        return {
          field: baseField,
          operator,
          value: value[1]
        };
      })
      .filter(Boolean) as QBFilter[];
  } catch (error) {
    console.warn('Error parsing filters from search params:', error);
    return [];
  }
};

export const mapQBFiltersToSearchParams = (filters: QBFilter[]): string => {
  const mapped: Record<string, [string, string]> = {};
  filters.forEach((filter) => {
    mapped[filter.field] = [filter.operator, String(filter.value)];
  });
  return JSON.stringify(mapped);
};

export const mapQBFiltersToAPIParams = (
  filters: QBFilter[]
): Record<string, string> => {
  const apiParams: Record<string, string> = {};
  filters.forEach((filter) => {
    const { field, operator, value } = filter;
    let op = operator;
    let val = String(value);

    switch (operator) {
      case QBOperator.Eq:
        op = '';
        break;
      case QBOperator.IsNull:
        val = 'true';
        break;
      // the API only supports the "__isnull" boolean filter, so "is not empty" maps to __isnull=false
      case QBOperator.IsNotNull:
        op = QBOperator.IsNull;
        val = 'false';
        break;
      case QBOperator.ContainedBy:
      case QBOperator.Overlap:
        val = `["${val}"]`;
        break;
      case QBOperator.Contains:
        val = `{${val}}`;
        break;
      default:
        break;
    }

    const apiKey = `${field}${op}`;
    apiParams[apiKey] = val;
  });
  return apiParams;
};

// convert the response from the filter definitions API into the shape needed for the filter config, which is QBFiltersConfig
// This also formats relationship field name from something like "user__name" to "User.Name" for better readability in the UI
export const mapQBFilterDefinitionsToConfig = (
  definitions: QBFilterDefinitionsResponseDTO,
  customLabels: Record<string, string> = {},
  hiddenFields?: string[],
  labelReplacements?: QBLabelReplacement[]
): QBFiltersConfig => {
  return Object.fromEntries(
    Object.entries(definitions.filters)
      .filter(([key]) => !hiddenFields?.includes(key))
      .map(([key, value]) => [
        key,
        {
          label: labelReplacements
            ? labelReplacements.reduce(
                (label, { pattern, replacement }) =>
                  label.replace(pattern, replacement),
                get(
                  customLabels,
                  key,
                  key?.split('__').map(startCase).join('.')
                )
              )
            : get(customLabels, key, key?.split('__').map(startCase).join('.')),
          // the API only exposes "__isnull", so add "is not empty" as its display-only counterpart
          operators:
            value.operators?.includes(QBOperator.IsNull) &&
            !value.operators.includes(QBOperator.IsNotNull)
              ? [...value.operators, QBOperator.IsNotNull]
              : value.operators || [],
          type: value.type,
          showCounts: key !== 'user__internal'
        }
      ])
  );
};

// convert the response from the group by options API into the shape needed for the filter options in the config, which is Record<string, QBFilterOptions[]>
export const mapQBGroupByOptions = (
  options: Record<string, Record<string, number>>,
  labelFormatter?: (label: string) => string
): Record<string, QBFilterOptions[]> => {
  const result: Record<string, QBFilterOptions[]> = {};
  Object.entries(options).forEach(([field, values]) => {
    result[field] = Object.entries(values).map(([v, count]) => {
      const label =
        v === 'Null' || v === ''
          ? `Is Empty`
          : labelFormatter
            ? labelFormatter(v)
            : v;

      return {
        value: v,
        label,
        count
      };
    });
  });
  return result;
};
